import React, { useEffect, useMemo, useState } from "react";
import { Palette, Moon, Sun, RefreshCw, Save, Monitor } from "lucide-react";
import TenantPreferencesService, {
  ThemeConfig,
} from "../../_services/TenantPreferencesService";
import { THEME_PRESETS, PRESET_DEFAULTS, ThemePreset } from "../../theme/presets.generated";
import { resolveTheme, applyTheme } from "../../theme/themeEngine";

interface Props {
  onError: (msg: string | null) => void;
  onSuccess: (msg: string | null) => void;
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const DEFAULT_PRESET = PRESET_DEFAULTS.iteach || "indigo";

/**
 * Theme tab — tenant preset picker + brand color overrides + dark-mode policy.
 * Persists to the S3 app-properties.json `theme` block via
 * PUT /v1/tenant-config/theme; both iteach and ilearn apply it at boot
 * (src/theme/themeEngine). Saving also re-themes iteach immediately.
 */
const ThemeTab: React.FC<Props> = ({ onError, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [presetId, setPresetId] = useState<string>(DEFAULT_PRESET);
  const [primary, setPrimary] = useState<string>("");
  const [accent, setAccent] = useState<string>("");
  const [darkEnabled, setDarkEnabled] = useState(false);
  const [darkDefault, setDarkDefault] = useState<"light" | "dark" | "system">("system");
  const [previewMode, setPreviewMode] = useState<"light" | "dark">("light");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await TenantPreferencesService.getThemeConfig();
        if (cancelled) return;
        if (cfg) {
          setPresetId(cfg.preset || DEFAULT_PRESET);
          setPrimary(cfg.overrides?.primary || "");
          setAccent(cfg.overrides?.accent || "");
          setDarkEnabled(cfg.darkMode?.enabled === true);
          setDarkDefault(cfg.darkMode?.default || "system");
        }
      } catch (e: any) {
        onError(e?.response?.data?.error?.message || e?.message || "Failed to load theme config");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedPreset: ThemePreset | undefined = THEME_PRESETS.find((p) => p.id === presetId);

  const draft: ThemeConfig = useMemo(
    () => ({
      preset: presetId,
      overrides: {
        ...(HEX_RE.test(primary) ? { primary } : {}),
        ...(HEX_RE.test(accent) ? { accent } : {}),
      },
      darkMode: { enabled: darkEnabled, default: darkDefault },
    }),
    [presetId, primary, accent, darkEnabled, darkDefault],
  );

  // Live preview vars — resolveTheme is the exact runtime path, so what you
  // see here is what both apps render.
  const previewVars = useMemo(() => {
    const resolved = resolveTheme(draft as any);
    return previewMode === "dark" ? resolved.brandVarsDark : resolved.brandVarsLight;
  }, [draft, previewMode]);

  const pickPreset = (p: ThemePreset) => {
    setPresetId(p.id);
    // switching preset discards color overrides — they were relative to the old one
    setPrimary("");
    setAccent("");
  };

  const save = async () => {
    onError(null);
    if (primary && !HEX_RE.test(primary)) return onError("Primary override must be a #rrggbb hex color");
    if (accent && !HEX_RE.test(accent)) return onError("Accent override must be a #rrggbb hex color");
    setSaving(true);
    try {
      const saved = await TenantPreferencesService.saveThemeConfig(draft);
      applyTheme(saved as any); // re-theme iteach right now (also refreshes the boot cache)
      onSuccess(
        "Theme saved. iteach is re-themed now; ilearn picks it up on the next page load (app-properties.json is uploaded with no-cache).",
      );
    } catch (e: any) {
      onError(e?.response?.data?.error?.message || e?.message || "Failed to save theme");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="tp-loading">Loading theme…</div>;

  const effectivePrimary = HEX_RE.test(primary) ? primary : selectedPreset?.brand.primary || "#667eea";
  const effectiveAccent = HEX_RE.test(accent) ? accent : selectedPreset?.brand.accent || "#764ba2";

  return (
    <div className="tp-theme">
      {/* ---- Preset gallery ---- */}
      <div className="tp-card">
        <h3>
          <Palette size={16} style={{ marginRight: 6, verticalAlign: "-3px" }} />
          Theme preset
        </h3>
        <p className="tp-card-desc">
          One theme for both apps — teachers (iteach) and students (ilearn). Colors derive from the
          preset&apos;s primary/accent pair; everything else (surfaces, text, status colors) follows
          automatically in light and dark.
        </p>
        <div className="tp-theme-grid">
          {THEME_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`tp-theme-swatch ${p.id === presetId ? "selected" : ""}`}
              onClick={() => pickPreset(p)}
            >
              <span
                className="tp-theme-swatch-strip"
                style={{
                  background: `linear-gradient(135deg, ${p.brand.primary} 0%, ${p.brand.accent} 100%)`,
                }}
              />
              <span className="tp-theme-swatch-name">{p.name}</span>
              <span className="tp-theme-swatch-desc">{p.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ---- Overrides ---- */}
      <div className="tp-card">
        <h3>Brand color overrides</h3>
        <p className="tp-card-desc">
          Optional — fine-tune the selected preset with your institution&apos;s exact brand colors.
        </p>
        <div className="tp-theme-overrides">
          <label className="tp-theme-color-field">
            <span>Primary</span>
            <input
              type="color"
              value={effectivePrimary}
              onChange={(e) => setPrimary(e.target.value)}
            />
            <input
              type="text"
              placeholder={selectedPreset?.brand.primary}
              value={primary}
              maxLength={7}
              onChange={(e) => setPrimary(e.target.value.trim())}
            />
          </label>
          <label className="tp-theme-color-field">
            <span>Accent</span>
            <input
              type="color"
              value={effectiveAccent}
              onChange={(e) => setAccent(e.target.value)}
            />
            <input
              type="text"
              placeholder={selectedPreset?.brand.accent}
              value={accent}
              maxLength={7}
              onChange={(e) => setAccent(e.target.value.trim())}
            />
          </label>
          {(primary || accent) && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setPrimary("");
                setAccent("");
              }}
            >
              <RefreshCw size={13} style={{ marginRight: 4, verticalAlign: "-2px" }} />
              Reset to preset
            </button>
          )}
        </div>
      </div>

      {/* ---- Dark mode ---- */}
      <div className="tp-card">
        <h3>
          <Moon size={16} style={{ marginRight: 6, verticalAlign: "-3px" }} />
          Dark mode
        </h3>
        <p className="tp-card-desc">
          When enabled, users get a light/dark toggle (their choice is remembered per browser).
          Exam players and printed pages always stay light.
        </p>
        <label className="tp-theme-dark-toggle">
          <input
            type="checkbox"
            checked={darkEnabled}
            onChange={(e) => setDarkEnabled(e.target.checked)}
          />
          <span>Allow dark mode for this tenant</span>
        </label>
        {darkEnabled && (
          <label className="tp-theme-dark-default">
            <span>Default appearance (before the user chooses):</span>
            <select
              value={darkDefault}
              onChange={(e) => setDarkDefault(e.target.value as "light" | "dark" | "system")}
            >
              <option value="system">Follow device setting</option>
              <option value="light">Light</option>
              <option value="dark">Dark</option>
            </select>
          </label>
        )}
      </div>

      {/* ---- Live preview ---- */}
      <div className="tp-card">
        <h3>Preview</h3>
        <div className="tp-theme-preview-tabs">
          <button
            type="button"
            className={`tp-theme-preview-tab ${previewMode === "light" ? "active" : ""}`}
            onClick={() => setPreviewMode("light")}
          >
            <Sun size={13} style={{ marginRight: 4, verticalAlign: "-2px" }} />
            Light
          </button>
          <button
            type="button"
            className={`tp-theme-preview-tab ${previewMode === "dark" ? "active" : ""}`}
            onClick={() => setPreviewMode("dark")}
          >
            <Moon size={13} style={{ marginRight: 4, verticalAlign: "-2px" }} />
            Dark
          </button>
        </div>
        <div
          className="tp-theme-preview"
          data-theme={previewMode}
          style={previewVars as React.CSSProperties}
        >
          <div className="tp-theme-preview-nav" style={{ background: "var(--gradient-brand)" }}>
            <Monitor size={14} color="var(--text-on-brand)" />
            <span>Navigation header</span>
          </div>
          <div className="tp-theme-preview-body" style={{ background: "var(--surface-page)" }}>
            <div
              className="tp-theme-preview-card"
              style={{
                background: "var(--surface-card)",
                border: "1px solid var(--border-default)",
              }}
            >
              <div style={{ color: "var(--text-strong)", fontWeight: 700, fontSize: 14 }}>
                Card title
              </div>
              <div style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 10 }}>
                Muted supporting copy with a{" "}
                <span style={{ color: "var(--text-link)", textDecoration: "underline" }}>link</span>
              </div>
              <span
                style={{
                  background: "var(--brand-primary)",
                  color: "var(--text-on-brand)",
                  borderRadius: 6,
                  padding: "5px 14px",
                  fontSize: 12,
                  fontWeight: 600,
                  display: "inline-block",
                  marginRight: 8,
                }}
              >
                Primary action
              </span>
              <span
                style={{
                  background: "var(--brand-primary-subtle)",
                  color: "var(--brand-primary)",
                  borderRadius: 999,
                  padding: "4px 12px",
                  fontSize: 11,
                  fontWeight: 600,
                  display: "inline-block",
                }}
              >
                Active pill
              </span>
              <div style={{ marginTop: 12, display: "flex", gap: 6 }}>
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <span
                    key={n}
                    title={`chart-${n}`}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 4,
                      display: "inline-block",
                      background: `var(--chart-${n})`,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tp-theme-actions">
        <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>
          <Save size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} />
          {saving ? "Saving…" : "Save theme"}
        </button>
      </div>
    </div>
  );
};

export default ThemeTab;
